import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const STEP_ICONS = [
  <svg key="0" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>,
  <svg key="1" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>,
  <svg key="2" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  <svg key="3" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  <svg key="4" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
];

const DEFAULT_STEP_LABELS = ["Specialty", "Details", "Evaluation", "Contact", "Confirmed"];

interface ChatStepperProps {
  currentStep: number;
  dir?: "ltr" | "rtl";
  stepLabels?: string[];
}

export function ChatStepper({ currentStep, dir = "ltr", stepLabels = DEFAULT_STEP_LABELS }: ChatStepperProps) {
  const steps = stepLabels.map((label) => ({ label }));
  return (
    <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-2.5 border-b border-border/20 bg-card/30 backdrop-blur-sm" dir={dir}>
      {steps.map((step, i) => (
        <div key={`${step.label}-${i}`} className="flex items-center flex-1 min-w-0">
          {i > 0 && (
            <div className={cn("flex-1 h-[2px] mx-1 min-w-[8px] transition-colors duration-500", i <= currentStep ? "bg-primary" : "bg-border/30")} />
          )}
          <motion.div
            className="flex flex-col items-center gap-1.5 flex-shrink-0"
            animate={{ scale: i === currentStep ? 1 : 0.9, opacity: i <= currentStep ? 1 : 0.4 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center transition-colors duration-300",
                i < currentStep
                  ? "bg-primary text-primary-foreground"
                  : i === currentStep
                    ? "bg-amber-100 border-2 border-amber-500 text-amber-700 dark:bg-primary/20 dark:border-primary dark:text-primary"
                    : "bg-muted/30 border border-border text-muted-foreground"
              )}
              animate={i === currentStep ? { scale: [1, 1.1, 1] } : {}}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            >
              {i < currentStep ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
              ) : (
                STEP_ICONS[i]
              )}
            </motion.div>
            <span className={cn("text-[9px] font-medium hidden md:block", i <= currentStep ? "text-foreground" : "text-muted-foreground")}>
              {step.label}
            </span>
          </motion.div>
          {i < steps.length - 1 && <div className="flex-1 min-w-[8px]" />}
        </div>
      ))}
    </div>
  );
}
